import { useState, useCallback, useMemo, useEffect } from 'react'
import { Alert, Button, GenericDialog } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import {
  ImportedGood,
  BulkUpdateImportedGoodsUpdates,
  useBulkUpdateImportedGoods
} from '@/features/cbam-reporting/hooks/importedGoods'
import { Partnership } from '@/features/suppliers/hooks/partnerships'
import { Installation } from '@/features/suppliers/hooks/installations'
import { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'
import toast from 'react-hot-toast'
import { useGoodsCodes } from '@/shared/hooks/goodsCodes'
import BulkUpdateSupplierEmissionsForm, {
  type EligibilityMessages
} from '../components/bulkUpdateSupplierEmissionsForm/BulkUpdateSupplierEmissionsForm'
import {
  BulkUpdateSupplierEmissionsFormData,
  getInitialFormData,
  validateBulkUpdateSupplierEmissionsForm,
  EMPTY_VALUE
} from '../components/bulkUpdateSupplierEmissionsForm/BulkUpdateSupplierEmissionsForm.schema'

export default function BulkUpdateSupplierEmissionsDialog({
  isOpen,
  close,
  selectedGoods,
  organizationId,
  cbamReportId,
  partnerships,
  installations,
  emissionsReports,
  onSuccess,
  cbamReportYear
}: {
  isOpen: boolean
  close: () => void
  selectedGoods: ImportedGood[]
  organizationId: string
  cbamReportId: string
  partnerships: Partnership[]
  installations: Installation[]
  emissionsReports: SupplierEmissionsReport[]
  onSuccess: () => void
  cbamReportYear?: number
}) {
  const { t } = useI18n()
  const { data: goodsCodes } = useGoodsCodes({})
  const { mutateAsync: bulkUpdate, isPending } = useBulkUpdateImportedGoods(organizationId, cbamReportId)

  // Compute common values from selected goods for pre-filling
  const initialFormData = useMemo(() => {
    if (selectedGoods.length === 0) {
      return getInitialFormData()
    }

    // Check if all goods have the same installationId
    const installationIds = selectedGoods
      .map(good => good.installationId)
      .filter((id): id is string => !!id)
    const commonInstallationId =
      installationIds.length === selectedGoods.length &&
      installationIds.every(id => id === installationIds[0])
        ? installationIds[0]
        : ''

    // If we have a common installation, get its partnershipId
    let commonPartnershipId = ''
    if (commonInstallationId) {
      const installation = installations.find(inst => inst.id === commonInstallationId)
      if (installation) {
        commonPartnershipId = installation.partnershipId
      }
    } else {
      // Otherwise, check if all goods have the same partnershipId
      const partnershipIds = selectedGoods
        .map(good => good.partnershipId)
        .filter((id): id is string => !!id)
      if (
        partnershipIds.length === selectedGoods.length &&
        partnershipIds.every(id => id === partnershipIds[0])
      ) {
        commonPartnershipId = partnershipIds[0]
      }
    }

    // Check if all goods have the same supplierEmissionsReportId
    const emissionsReportIds = selectedGoods
      .map(good => good.supplierEmissionsReportId)
      .filter((id): id is string => !!id)
    const commonEmissionsReportId =
      emissionsReportIds.length === selectedGoods.length &&
      emissionsReportIds.every(id => id === emissionsReportIds[0])
        ? emissionsReportIds[0]
        : ''

    return {
      partnershipId: commonPartnershipId,
      installationId: commonInstallationId,
      supplierEmissionsReportId: commonEmissionsReportId
    }
  }, [selectedGoods, installations])

  const [formData, setFormData] = useState<BulkUpdateSupplierEmissionsFormData>(initialFormData)
  const [eligibilityMessages, setEligibilityMessages] = useState<EligibilityMessages>({
    emissionsTooltipKey: null,
    noReportsAvailableWarningKey: null
  })

  // Reset form when dialog opens/closes or selectedGoods changes
  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData)
    }
  }, [isOpen, initialFormData])

  const getAggregatedCategoryForCnCode = useMemo(() => {
    const byCnCode = new Map(
      (goodsCodes ?? []).map(goodsCode => [
        goodsCode.goodsCode,
        goodsCode.aggregatedGoodsCategory
      ])
    )
    return (cnCode: string) => byCnCode.get(cnCode)
  }, [goodsCodes])

  const resetForm = useCallback(() => {
    setFormData(initialFormData)
    setEligibilityMessages({ emissionsTooltipKey: null, noReportsAvailableWarningKey: null })
  }, [initialFormData])

  const handleClose = useCallback(() => {
    resetForm()
    close()
  }, [close, resetForm])

  const buildUpdates = useCallback((): BulkUpdateImportedGoodsUpdates => {
    const updates: BulkUpdateImportedGoodsUpdates = {}
    if (formData.partnershipId) {
      updates.partnershipId = formData.partnershipId === EMPTY_VALUE ? null : formData.partnershipId
    }
    if (formData.installationId) {
      updates.installationId =
        formData.installationId === EMPTY_VALUE ? null : formData.installationId
    }
    if (formData.supplierEmissionsReportId) {
      updates.supplierEmissionsReportId =
        formData.supplierEmissionsReportId === EMPTY_VALUE
          ? null
          : formData.supplierEmissionsReportId
    }
    return updates
  }, [formData])

  const isFormValid = useMemo(
    () => validateBulkUpdateSupplierEmissionsForm(formData).success,
    [formData]
  )

  const handleSubmit = useCallback(async () => {
    try {
      const updates = buildUpdates()
      await bulkUpdate({
        ids: selectedGoods.map(good => good.id),
        updates
      })
      toast.success(
        t('cbamReporting:bulkUpdate.notifications.success', { count: selectedGoods.length })
      )
      onSuccess()
      handleClose()
    } catch (error) {
      console.error('Bulk update supplier & emissions failed:', error)
      toast.error(t('cbamReporting:bulkUpdate.notifications.error'))
    }
  }, [bulkUpdate, selectedGoods, buildUpdates, t, onSuccess, handleClose])

  return (
    <GenericDialog
      isOpen={isOpen}
      close={handleClose}
      title={t('cbamReporting:bulkUpdate.supplierEmissionsDialog.title')}
      subtitle={t('cbamReporting:bulkUpdate.supplierEmissionsDialog.subtitle', {
        count: selectedGoods.length
      })}
      size="2xl"
    >
      <div className="space-y-4">
        <p className="text-sm text-text-secondary">{t('cbamReporting:bulkUpdate.description')}</p>
        {eligibilityMessages.emissionsTooltipKey && (
          <Alert type="warning">{t(eligibilityMessages.emissionsTooltipKey)}</Alert>
        )}
        {eligibilityMessages.noReportsAvailableWarningKey &&
          !eligibilityMessages.emissionsTooltipKey && (
            <Alert type="warning">{t(eligibilityMessages.noReportsAvailableWarningKey)}</Alert>
          )}
        <BulkUpdateSupplierEmissionsForm
          data={formData}
          onChange={setFormData}
          selectedGoods={selectedGoods}
          partnerships={partnerships}
          installations={installations}
          emissionsReports={emissionsReports}
          getAggregatedCategoryForCnCode={getAggregatedCategoryForCnCode}
          onEligibilityMessagesChange={setEligibilityMessages}
          cbamReportYear={cbamReportYear}
        />
        <footer className="flex justify-end gap-2">
          <Button variant="neutral" onClick={handleClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!isFormValid || isPending} isLoading={isPending}>
            {t('cbamReporting:bulkUpdate.actions.update', { count: selectedGoods.length })}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
